import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import IntegrationPageSectionContext from '../StateManagement/IntegrationPageSectionContext';
import HtmlPageSectionComponent from './HtmlPageSectionComponent';
import AlertBox from '../../../components/AlertBox';
import CardFieldsConfigurator from '../CardFieldsConfigurator';
import { useLocation } from 'react-router';
import withAnchor from '../../../components/Anchor/withAnchor';
import { useSelector } from 'react-redux';
import { TREATMENTS, ENABLE_STUDIO_SHIPPING_CALLBACK } from '../../../constants/experimentMap';

const ComponentMap = {
  alertBox: AlertBox,
  iAcdcCardFieldsConfigurator: CardFieldsConfigurator
};

export default function IntegrationSteps({ ecmContent: { contents: ecmContents = [] } = {} }) {
  const {
    code: {
      language: { frontend, backend } = {},
      updateHighlightedCode,
      consolidatedCode,
      status,
      updateCurrentSectionKey
    },
    pageContent,
    ShippingCallbackConfigurator: { shippingCallbackType },
    SdkConfigurator: {
      sdkSettings: { intent = '' }
    },
    integrationType: type,
    codeHighlight: { currentSectionKey }
  } = useContext(IntegrationPageSectionContext);
  const [contents, setContents] = useState(ecmContents);
  const [observer, setObserver] = useState();
  const { hash } = useLocation();
  const experimentsSelector = (state) => state.app.experiments;
  const experiments = useSelector(experimentsSelector);
  const experimentValue = experiments?.studio_enable_shipping_callback;

  // Checks if the shipping callback experiment is enabled
  const isShippingCallbackEnabled =
    experimentValue === ENABLE_STUDIO_SHIPPING_CALLBACK?.TREATMENT_SHIPPING ||
    experimentValue === ENABLE_STUDIO_SHIPPING_CALLBACK.TREATMENT_BOTH;

  // Checks if the contact module experiment is enabled
  const isContactModuleEnabled =
    experimentValue === ENABLE_STUDIO_SHIPPING_CALLBACK?.TREATMENT_CONTACT ||
    experimentValue === ENABLE_STUDIO_SHIPPING_CALLBACK.TREATMENT_BOTH;

  const filteredContent = useMemo(() => {
    return contents.filter(({ contentJson = {} } = {}) => {
      const languagesEntry = contentJson?.languages;
      const typeEntry = contentJson?.integrationType;
      const ordersEntry = contentJson?.orders;
      const experimentEntry = contentJson?.experiment;
      const shippingCallbackTypeEntry = contentJson?.shippingCallbackType;
      let contentEnabled = true;
      if (languagesEntry) {
        if (languagesEntry.frontend) {
          contentEnabled = languagesEntry.frontend?.includes(frontend);
        }
        if (languagesEntry.backend) {
          contentEnabled = languagesEntry.backend?.includes(backend);
        }
      }
      if (typeEntry) {
        contentEnabled = typeEntry === type;
      }
      if (contentEnabled && ordersEntry) {
        if (ordersEntry.intent) {
          contentEnabled = ordersEntry.intent?.includes(intent);
        }
      }
      if (experimentEntry) {
        const experiment = Object.values(TREATMENTS).find((exp) => exp.NAME === experimentEntry);
        contentEnabled = experiments[experimentEntry] === experiment.TREATMENT;
      }
      if (shippingCallbackTypeEntry) {
        contentEnabled = shippingCallbackTypeEntry === shippingCallbackType;
      }
      // Hides shipping callbacks section that include a Client/Server toggle if experiment is off
      if (!isShippingCallbackEnabled && contentJson?.ShippingCallbackConfigurator) {
        return false;
      }
      // Hides Contact Module toggle section if experiment is off
      if (!isContactModuleEnabled && contentJson?.ContactModule) {
        return false;
      }
      return contentEnabled;
    });
  }, [contents, frontend, backend, intent, status, type, shippingCallbackType]);

  const getSection = (tag) => contents.find((section) => section.key === tag);

  const getCodeBlock = (key, filename) => {
    const codeBlocks = consolidatedCode.reduce((prev, curr) => {
      return [...prev, ...curr.metadata];
    }, []);
    const keyElm = key.split(',')[0];
    // Let's find the code for a contentKey and filename combination
    let codeBlock = codeBlocks.find((block) => block.key === keyElm && block.filename === filename);

    // If we don't find any, get the first code occurence for a contentKey
    if (!codeBlock) {
      codeBlock = codeBlocks.find((block) => block.key === keyElm);
    }

    return codeBlock;
  };

  const updateHighlight = (tag) => {
    const section = getSection(tag);
    if (!section) return;

    const { key, codeBlockKey, codeBlockFileName, contentJson = {} } = section;
    if (!codeBlockKey) return;
    const codeBlock = getCodeBlock(codeBlockKey, codeBlockFileName);

    if (!codeBlock) return;
    if (contentJson.CodeBlockToggle) {
      if (!status[codeBlock.key]) {
        if (currentSectionKey === key) {
          updateHighlightedCode({});
          updateCurrentSectionKey(undefined);
        }
        return;
      } else {
        if (contentJson.CodeBlockToggle?.selectable === false) {
          updateCurrentSectionKey(undefined);
          return;
        }
      }
    }
    updateHighlightedCode(codeBlock);
    updateCurrentSectionKey(key);
  };

  const isSelectableSection = (tag) => {
    const { codeBlockKey, codeBlockFileName } = getSection(tag);
    if (!codeBlockKey) return false;
    return !!getCodeBlock(codeBlockKey, codeBlockFileName);
  };

  const handleIntersection = async (sections) => {
    if (sections.length > 5) return; // When the intersection observer is initialized, sections contains all the observed sections. so if there is many, the call gets ignored
    const fullerSection = sections
      .filter((section) => isSelectableSection(section.target.dataset.tag)) // Filters out the non selectable sections.
      .filter((section) => section.isIntersecting);

    if (fullerSection.length > 0) {
      // If an element is found with the filter / find criterias, it will get updated.
      updateHighlight(fullerSection[0].target.dataset.tag);
    }
  };

  const updateObserver = () => {
    setObserver(
      new IntersectionObserver(handleIntersection, {
        threshold: 0.01,
        rootMargin: '-55% 0px -35% 0px'
      })
    );
  };

  const handleSectionClick = (tag) => {
    updateHighlight(tag);
  };

  const findFirstSelectable = () => {
    return filteredContent.find((section) => isSelectableSection(section.key))?.key;
  };

  useEffect(() => {
    if (filteredContent.length > 0) {
      let selectedKey;
      // Check if anchor exists, and find content with matching anchor
      if (hash) {
        const matchingAnchor = filteredContent.find((section) => section.anchor === hash.slice(1));
        selectedKey = matchingAnchor ? matchingAnchor.key : findFirstSelectable();
      } else {
        selectedKey = findFirstSelectable();
      }
      if (selectedKey) {
        updateHighlight(selectedKey);
      }
    }
  }, []);

  useEffect(() => {
    // Updates the observer when any configuration is changed.
    updateObserver();
  }, [frontend, backend, status, type]);

  useEffect(() => {
    if (!pageContent || pageContent.length === 0) return;
    setContents(pageContent);
    updateObserver();
  }, [pageContent, filteredContent]);

  useEffect(() => {
    // Every time the observer gets upadted. updates the sections to observe.
    if (!observer) return;
    document.querySelectorAll('[data-tag]').forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer?.disconnect();
    };
  }, [observer, filteredContent]);

  useEffect(() => {
    // If the current section gets updated with a new key (with click), updates the highlighted with the html elements.
    if (!currentSectionKey) return;
    const section = getSection(currentSectionKey);
    const codeBlock = getCodeBlock(section.codeBlockKey, section.codeBlockFileName);
    updateHighlightedCode(codeBlock);
  }, [currentSectionKey]);

  return (
    <>
      {filteredContent.map((content) => {
        const { id, key, contentDetails, anchor, ...rest } = content;
        const Component = ComponentMap[key];
        if (Component) {
          return (
            <Component
              key={id}
              ecmContent={{
                content: contentDetails,
                ...rest
              }}
              disablePadding
            />
          );
        }

        const componentProps = {
          ecmContent: content,
          highlighted: key === currentSectionKey,
          onClick: handleSectionClick,
          updateObserver
        };
        if (anchor) {
          const handleAnchorClick = () => {
            updateObserver();
            handleSectionClick(key);
          };
          componentProps.anchor = {
            value: anchor,
            visible: true,
            onAnchorClick: handleAnchorClick
          };
          return withAnchor(HtmlPageSectionComponent)(componentProps);
        }
        return <HtmlPageSectionComponent key={key} {...componentProps} />;
      })}
    </>
  );
}

IntegrationSteps.propTypes = {
  ecmContent: PropTypes.object.isRequired,
  experiments: PropTypes.object.isRequired
};
